import { useEffect, useRef } from 'react';
import { useNotifications } from '../context/NotificationContext';

const NOTIFICATION_WINDOW = 24 * 60 * 60 * 1000; // Fenêtre de notification de 24 heures

export const useNotificationChecker = () => {
  const { state, addNotification } = useNotifications();
  const notifiedRef = useRef<Set<string>>(new Set());
  
  // Vérifier les notifications à afficher
  useEffect(() => {
    // Filtrer les notifications qui n'ont pas encore été notifiées
    const notificationsToShow = state.notifications.filter(notification => {
      // Vérifier si la notification est active et n'a pas déjà été notifiée
      const shouldNotify = notification.isActive && !notifiedRef.current.has(notification.id);
      
      // Filtrer les notifications de type "Suppression annulée"
      const isCancelledDeletion = notification.type === 'info' && 
        notification.title === 'Suppression annulée';
      
      // Filtrer les notifications de changement de catégorie
      const isCategoryChange = notification.type === 'info' && 
        notification.message && 
        notification.message.includes('a changé de catégorie');
      
      // Pour les rappels, vérifier si la date est arrivée ou proche
      let isTimeToNotify = true;
      if (notification.type === 'reminder' && notification.reminderDate) {
        const reminderDate = new Date(notification.reminderDate);
        const now = new Date();
        
        // Ne notifier que si la date de rappel est passée ou à moins de 5 minutes
        const timeDiff = reminderDate.getTime() - now.getTime();
        isTimeToNotify = timeDiff <= 5 * 60 * 1000; // 5 minutes en millisecondes
        
        // Log pour le débogage
        console.log(`Vérification du rappel ${notification.id}:`, {
          titre: notification.title,
          dateRappel: reminderDate.toLocaleString(),
          maintenant: now.toLocaleString(),
          différenceTemps: Math.round(timeDiff / 60000) + ' minutes',
          notifier: isTimeToNotify
        });
      }
      
      // Ajouter l'ID à la liste des notifications déjà traitées
      if (shouldNotify && isTimeToNotify && !isCancelledDeletion && !isCategoryChange) {
        notifiedRef.current.add(notification.id);
        return true;
      }
      
      // Ne pas afficher les notifications de suppression annulée ou de changement de catégorie
      // ou les rappels dont la date n'est pas encore arrivée
      return false;
    });
    
    // Traiter les notifications à afficher
    notificationsToShow.forEach(notification => {
      // Vérifier si c'est un rappel et si la date est proche
      if (notification.type === 'reminder' && notification.reminderDate) {
        const reminderDate = new Date(notification.reminderDate);
        const now = new Date();
        const timeDiff = reminderDate.getTime() - now.getTime();
        
        // Si la date de rappel est dans le futur mais à moins de 24h
        if (timeDiff > 0 && timeDiff < NOTIFICATION_WINDOW) {
          // Formater la date pour l'affichage
          const formattedDate = reminderDate.toLocaleString();
          
          // Créer un message personnalisé pour le rappel
          const message = `Rappel prévu pour le ${formattedDate}`;
          
          // Afficher la notification via l'API Notification du navigateur
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: message,
              icon: '/logo192.png'
            });
          } else if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                new Notification(notification.title, {
                  body: message,
                  icon: '/logo192.png'
                });
              }
            });
          }
        }
      } else {
        // Pour les notifications normales, afficher via l'API Notification du navigateur
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.message,
            icon: '/logo192.png'
          });
        } else if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              new Notification(notification.title, {
                body: notification.message,
                icon: '/logo192.png'
              });
            }
          });
        }
      }
    });
  }, [state.notifications, addNotification]);

  return null;
};

